import React, { useState, useEffect, useRef } from "react";
import {
	DndContext,
	useSensor,
	useSensors,
	PointerSensor,
	closestCenter,
	useDraggable,
	useDroppable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { useEventStore, SeatAssignmentUtils } from "../../../store/eventStore";
import { SEAT_STYLES } from "../../../pages/Ticket/Daily/DailyDetailModal";

const OBJECT_LABELS = {
	toilet: "WC",
	door: "DOOR",
	vestibule: "VEST",
	luggage: "LUGGAGE",
	table: "TABLE",
	wheelchair: "WHEEL",
	wheelchair_space: "WHEEL",
	bulkhead: "BULK",
	blocked: "BLOCK",
	kiosk: "KIOSK",
	aed: "AED",
	stage: "무대",
	wine_bar: "와인바",
	broadcast_room: "방송실",
	partition: "구분선",
};

const OBJECT_STYLES = {
	container:
		"w-16 h-12 bg-gray-100 border border-gray-300 rounded-md shadow-sm flex items-center justify-center text-[10px] text-gray-600",
	table:
		"w-16 h-8 bg-amber-100 border border-amber-300 rounded-md flex items-center justify-center text-[10px] text-amber-700",
	wheelchairForward:
		"w-16 h-12 bg-blue-50 border border-blue-300 rounded-md flex flex-col items-center justify-center text-[10px] text-blue-700",
	wheelchairBackward:
		"w-16 h-12 bg-rose-50 border border-rose-300 rounded-md flex flex-col items-center justify-center text-[10px] text-rose-700",
	stage:
		"w-16 h-12 bg-purple-100 border border-purple-400 rounded-md flex items-center justify-center text-[10px] text-purple-700 font-bold",
	wine_bar:
		"w-16 h-12 bg-red-100 border border-red-400 rounded-md flex items-center justify-center text-[10px] text-red-700 font-bold",
	broadcast_room:
		"w-16 h-12 bg-green-100 border border-green-400 rounded-md flex items-center justify-center text-[10px] text-green-700 font-bold",
};

export default function AssignModal({
	isOpen,
	onClose,
	searchType,
	toAssignData,
}) {
	// dnd-kit 센서 설정
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // 8px 이상 움직여야 드래그 시작
			},
		})
	);

	// =====================================
	// State 관리
	// =====================================
	const [engines, setEngines] = useState([]);
	const [selectedEngine, setSelectedEngine] = useState("");
	const [seatData, setSeatData] = useState({});
	const [seatLayout, setSeatLayout] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isAssignmentConfirmed, setIsAssignmentConfirmed] = useState(false);
	const [timer, setTimer] = useState({ minutes: 0, seconds: 0 });
	const [showModal, setShowModal] = useState(false);
	const [modalType, setModalType] = useState(""); // 'exec' | 'cancel'
	const [userSeatNumbers, setUserSeatNumbers] = useState({});
	const [teamCounts, setTeamCounts] = useState({});

	// 드래그 앤 드롭 관련 상태
	const [draggedSeat, setDraggedSeat] = useState(null);
	const [dragOverSeat, setDragOverSeat] = useState(null);

	const { getEngines, runSeatAssignment, confirmSeatAssignment } =
		useEventStore();

	// 타이머 관련 refs
	const startTimeRef = useRef(null);
	const timerIntervalRef = useRef(null);
	const tableId = "K000000043"; // ktx 고정값

	// =====================================
	// 초기화 및 데이터 로딩
	// =====================================

	useEffect(() => {
		if (isOpen) {
			setIsLoading(true);
			showExecutionModal();
			setSeatData({});
			setSeatLayout(null);

			initializeComponent();
		}

		return () => {
			if (timerIntervalRef.current) {
				clearInterval(timerIntervalRef.current);
			}
		};
	}, [isOpen]);

	// 좌석 데이터가 변경될 때마다 사용자별 좌석 순번과 팀 카운트 계산
	useEffect(() => {
		if (!seatData) return;

		const newUserSeatNumbers = {};
		const newTeamCounts = {};

		// 모든 호차에 대해 사용자별 좌석 정보 수집
		Object.entries(seatData).forEach(([carNo, carData]) => {
			Object.values(carData.data || {}).forEach((seatInfo) => {
				if (seatInfo.seat && seatInfo.seat !== "") {
					const userId = seatInfo.seat.split("@@")[0];

					// 사용자별 좌석 ID 목록 업데이트
					if (!newUserSeatNumbers[userId]) {
						newUserSeatNumbers[userId] = [];
					}
					newUserSeatNumbers[userId].push(seatInfo.id);

					// 사용자별 총 좌석 수 업데이트
					if (!newTeamCounts[userId]) {
						newTeamCounts[userId] = 0;
					}
					newTeamCounts[userId]++;
				}
			});
		});

		setUserSeatNumbers(newUserSeatNumbers);
		setTeamCounts(newTeamCounts);
	}, [seatData]);

	if (!isOpen) return null;

	const initializeComponent = async () => {
		try {
			await loadEngineList();
			if (toAssignData) {
				await handleRunSeatAssignment();
			}
		} catch (error) {
			console.error("컴포넌트 초기화 실패:", error);
		}
	};

	const loadEngineList = async () => {
		try {
			const response = await getEngines();
			if (response.success) {
				setEngines(response.data || []);

				// 엔진 데이터가 있으면 첫 번째 엔진 선택
				if (toAssignData.engineData) {
					setSelectedEngine(toAssignData.engineData.SEQ_NUM);
				} else if (response.data && response.data.length > 0) {
					setSelectedEngine(response.data[0].SEQ_NUM);
				}
			} else {
				throw new Error(
					response.error || "엔진 정보를 불러올 수 없습니다."
				);
			}
		} catch (error) {
			console.error("엔진 정보 로드 실패:", error);
			alert("엔진 정보를 불러올 수 없습니다.");
		}
	};

	// =====================================
	// 좌석배치 실행 관련 함수들
	// =====================================

	const handleRunSeatAssignment = async () => {
		setIsLoading(true);
		showExecutionModal();

		try {
			const seatmapLayout = toAssignData?.seatmapLayout || null;
			const ctmStr = toAssignData.assignData
				.map((data) => {
					const resStr = `${data.item.RES_DAY}-${data.item.RES_SEQ}`;
					const eventStr = `${data.item.eventId}`;
					const resName = `${data.item.RES_NM}`;
					// 휴대폰 뒷자리 4자리 추출
					const phoneLast4 = data.item.HANDPHONE3 ? data.item.HANDPHONE3.slice(-4) : "";
					const assignCnt = `${data.assign_count}`;

					return `${resStr}@@${eventStr}@@${resName}@@${phoneLast4}@@${tableId},${assignCnt}`;
				})
				.join("///");

			const assignmentParams = {
				date: SeatAssignmentUtils.formatDateString(
					toAssignData.eventData[0].start_day
				),
				trainCode: toAssignData.trainData.train_code,
				tableId: tableId,
				seatmapLayoutId: seatmapLayout?.id || null,
				trainTypeCode: seatmapLayout?.train_type_code || null,
				seatmapVariant: seatmapLayout?.variant || null,
				deptStn: toAssignData.trainData.dept_stn,
				arvStn: toAssignData.trainData.arrv_stn,
				ctmStr: ctmStr,
				engVer: selectedEngine || toAssignData.engineData.SEQ_NUM,
				ticketItemIds: toAssignData.trainData.ticket_item_ids.join(","),
			};

			const result = await runSeatAssignment(assignmentParams);

			if (!result.success) {
				alert(result.message || "좌석배치 실행에 실패했습니다.");
				setSeatData({});
				setSeatLayout(null);
				return;
			}

			setSeatData(result.data?.seatStatus || {});
			setSeatLayout(result.data?.seatmapLayout || null);
		} catch (error) {
			console.error("좌석배치 실행 실패:", error);
			alert(error.message || "좌석배치 실행에 실패했습니다.");
		} finally {
			setIsLoading(false);

			hideExecutionModal();
		}
	};

	const handleConfirmSeatAssignment = async () => {
		if (!window.confirm("현재 좌석배치를 확정하시겠습니까?")) {
			alert("배치를 취소하셨습니다.");
			return;
		}

		try {
			const tableContent = getRedipsTableContent();

			if (!tableContent) {
				alert("테이블이 비어있습니다!");
				return;
			}

			const assignInfos =
				SeatAssignmentUtils.parseTableContent(tableContent);

			if (assignInfos.length === 0) {
				alert("배치된 좌석이 없습니다!");
				return;
			}

			const assignmentData = {
				assignInfos,
				isOutbound: searchType === "go",
			};

			const result = await confirmSeatAssignment(assignmentData);

			if (result.success) {
				alert("좌석배치가 확정되었습니다.");
				setIsAssignmentConfirmed(true);
        onClose();
			} else {
				alert(result.message || "좌석배치 확정에 실패했습니다.");
			}
		} catch (error) {
			console.error("좌석배치 확정 실패:", error);
			alert("좌석배치 확정에 실패했습니다.");
		}
	};

	// =====================================
	// 모달 및 타이머 관련 함수들
	// =====================================

	const showExecutionModal = () => {
		setModalType("exec");
		setShowModal(true);
		startTimer();
	};

	const hideExecutionModal = () => {
		setModalType("cancel");
		setShowModal(false);
		stopTimer();
	};

	const startTimer = () => {
		startTimeRef.current = new Date();
		timerIntervalRef.current = setInterval(updateTimer, 1000);
	};

	const stopTimer = () => {
		if (timerIntervalRef.current) {
			clearInterval(timerIntervalRef.current);
			timerIntervalRef.current = null;
		}
	};

	const updateTimer = () => {
		if (startTimeRef.current) {
			const currentTime = new Date();
			const elapsedTime = Math.floor(
				(currentTime - startTimeRef.current) / 1000
			);
			const minutes = Math.floor(elapsedTime / 60);
			const seconds = elapsedTime % 60;

			setTimer({ minutes, seconds });
		}
	};

	// =====================================
	// 드래그 앤 드롭 관련 함수들
	// =====================================

	const getRedipsTableContent = () => {
		try {
			const assignments = [];

			Object.values(seatData).forEach((carData) => {
				Object.values(carData.data || {}).forEach((seatInfo) => {
					if (seatInfo.seat && seatInfo.seat !== "") {
						const userId = seatInfo.seat.split("@@")[0];
						assignments.push([`${seatInfo.id}:${userId}`]);
					}
				});
			});

			return JSON.stringify(assignments);
		} catch (error) {
			console.error("테이블 콘텐츠 추출 실패:", error);
			return null;
		}
	};

	// 좌석 데이터 업데이트 함수 (스왑 지원)
	const updateSeatData = (fromSeatId, toSeatId) => {
		try {
			// 새로운 좌석 데이터 객체 생성 (불변성 유지)
			const newSeatData = JSON.parse(JSON.stringify(seatData));

			console.log("updateSeatData", { fromSeatId, toSeatId });

			// 출발지 좌석과 목적지 좌석 정보 찾기
			let fromSeatInfo = null;
			let fromCarNo = null;
			let fromTargetKey = null;
			let toSeatInfo = null;
			let toCarNo = null;
			let toTargetKey = null;

			// 출발지 좌석과 목적지 좌석 찾기
			Object.entries(newSeatData).forEach(([carNo, carData]) => {
				Object.entries(carData.data || {}).forEach(
					([seatKey, seatInfo]) => {
						if (seatInfo.id === fromSeatId) {
							fromSeatInfo = { ...seatInfo };
							fromCarNo = carNo;
							fromTargetKey = seatKey;
						}
						if (seatInfo.id === toSeatId) {
							toSeatInfo = { ...seatInfo };
							toCarNo = carNo;
							toTargetKey = seatKey;
						}
					}
				);
			});

			// 좌석 정보가 모두 존재하는지 확인
			if (!fromSeatInfo || !toSeatInfo || !fromCarNo || !toCarNo || !fromTargetKey || !toTargetKey) {
				console.error("좌석 정보를 찾을 수 없습니다.");
				return;
			}

			console.log("fromSeatInfo", fromSeatInfo);
			console.log("toSeatInfo", toSeatInfo);

			// 출발지 좌석 데이터 백업
			const fromData = {
				seat: fromSeatInfo.seat,
				name: fromSeatInfo.name,
				last_phone: fromSeatInfo.last_phone,
				total_cnt: fromSeatInfo.total_cnt,
			};

			// 목적지 좌석 데이터 백업 (스왑용)
			const toData = {
				seat: toSeatInfo.seat || "",
				name: toSeatInfo.name || "",
				last_phone: toSeatInfo.last_phone || "",
				total_cnt: toSeatInfo.total_cnt || 0,
			};

			// 목적지 좌석에 출발지 데이터 복사
			newSeatData[toCarNo].data[toTargetKey].seat = fromData.seat;
			newSeatData[toCarNo].data[toTargetKey].name = fromData.name;
			newSeatData[toCarNo].data[toTargetKey].last_phone = fromData.last_phone;
			newSeatData[toCarNo].data[toTargetKey].total_cnt = fromData.total_cnt;
			newSeatData[toCarNo].data[toTargetKey].is_previous_seat = false;

			// 출발지 좌석에 목적지 데이터 복사 (스왑) - 비어있으면 비움
			newSeatData[fromCarNo].data[fromTargetKey].seat = toData.seat;
			newSeatData[fromCarNo].data[fromTargetKey].name = toData.name;
			newSeatData[fromCarNo].data[fromTargetKey].last_phone = toData.last_phone;
			newSeatData[fromCarNo].data[fromTargetKey].total_cnt = toData.total_cnt;
			if (toData.seat) {
				newSeatData[fromCarNo].data[fromTargetKey].is_previous_seat = false;
			}

			// 사용자별 좌석 순번 재계산
			const newUserSeatNumbers = {};
			const newTeamCounts = {};

			// 모든 호차에 대해 사용자별 좌석 정보 수집
			Object.entries(newSeatData).forEach(([carNo, carData]) => {
				Object.values(carData.data || {}).forEach((seatInfo) => {
					if (seatInfo.seat && seatInfo.seat !== "") {
						const userId = seatInfo.seat.split("@@")[0];

						// 사용자별 좌석 ID 목록 업데이트
						if (!newUserSeatNumbers[userId]) {
							newUserSeatNumbers[userId] = [];
						}
						newUserSeatNumbers[userId].push(seatInfo.id);

						// 사용자별 총 좌석 수 업데이트
						if (!newTeamCounts[userId]) {
							newTeamCounts[userId] = 0;
						}
						newTeamCounts[userId]++;
					}
				});
			});

			console.log("newSeatData", newSeatData);

			// 상태 업데이트
			setSeatData(newSeatData);
			setUserSeatNumbers(newUserSeatNumbers);
			setTeamCounts(newTeamCounts);

			// 시각적 피드백 - 좌석 이동 효과
			setTimeout(() => {
				const toElement = document.getElementById(toSeatId);
				if (toElement) {
					toElement.classList.add("bg-green-100");
					setTimeout(() => {
						toElement.classList.remove("bg-green-100");
					}, 500);
				}
			}, 0);

			console.log(`좌석 이동 완료: ${fromSeatId} -> ${toSeatId}`);
		} catch (error) {
			console.error("좌석 데이터 업데이트 중 오류 발생:", error);
		}
	};

	// =====================================
	// 이벤트 핸들러들
	// =====================================

	// dnd-kit 드래그 시작 핸들러
	const handleDragStart = (event) => {
		console.log("dnd-kit drag start", event);

		// 드래그된 좌석 정보 가져오기
		const draggedSeatId = event.active.id;
		const draggedSeatData = event.active.data?.current;

		// 이전 좌석은 드래그 불가
		if (draggedSeatData?.is_previous_seat) {
			return;
		}

		// 드래그 시작 시 상태 업데이트
		setDraggedSeat(draggedSeatId);
	};

	// dnd-kit 드래그 종료 핸들러
	const handleDragEnd = (event) => {
		console.log("dnd-kit drag end", event);

		// 드래그 종료 시 상태 초기화
		setDraggedSeat(null);
		setDragOverSeat(null);

		// 드롭 위치가 있는 경우 좌석 데이터 업데이트
		if (event.over) {
			const sourceId = event.active.id;
			const targetId = event.over.id;

			// 소스와 타겟이 같으면 아무 작업도 하지 않음
			if (sourceId === targetId) {
				return;
			}

			// 좌석 데이터 업데이트 로직 호출
			updateSeatData(sourceId, targetId);
		}
	};

	const handleEngineChange = (event) => {
		setSelectedEngine(event.target.value);
	};

	const handleRunAssignment = () => {
		if (!selectedEngine) {
			alert("엔진타입을 선택하세요");
			return;
		}
		handleRunSeatAssignment();
	};

	// =====================================
	// 렌더링
	// =====================================

	// 모달 바깥 영역 클릭 시 닫기 처리
	const handleOverlayClick = (e) => {
		// 클릭된 요소가 오버레이 자체인 경우에만 닫기
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const footer = (
		<div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6 flex items-center">
			{/* 왼쪽 그룹 - 엔진 선택 및 재실행 버튼 */}
			<div className="flex items-center space-x-2">
				<div className="relative">
					<select
						id="selectedYear"
						value={selectedEngine}
						onChange={handleEngineChange}
						className="py-2 pl-4 pr-8 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none"
					>
						{engines &&
							engines.length > 0 &&
							engines.map((engine, index) => (
								<option
									key={`${
										engine.SEQ_NUM || engine.seqNum
									}-${index}`}
									value={engine.SEQ_NUM || engine.seqNum}
								>
									{engine.DISP_NAME || engine.dispName}
								</option>
							))}
					</select>
					<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
						<svg
							className="fill-current h-4 w-4"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 20 20"
						>
							<path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
						</svg>
					</div>
				</div>

				<button
					onClick={handleRunAssignment}
					disabled={isLoading}
					className="inline-flex items-center justify-center gap-1 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
				>
					{isLoading ? (
						<>
							<svg
								className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								></circle>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
							실행 중...
						</>
					) : (
						<>
							<ArrowPathIcon
								className="h-5 w-5 mr-1"
								aria-hidden="true"
							/>
							좌석 배치 재실행
						</>
					)}
				</button>
			</div>

			{/* 스페이서 - 가운데 공간을 차지하여 양쪽 그룹을 분리 */}
			<div className="flex-grow"></div>

			{/* 오른쪽 그룹 - 취소 및 확정 버튼 */}
			<div className="flex items-center space-x-2">
				<button
					type="button"
					className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
					onClick={onClose}
				>
					취소
				</button>
				<button
					onClick={handleConfirmSeatAssignment}
					disabled={isAssignmentConfirmed}
					className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
				>
					좌석배치확정
				</button>
			</div>
		</div>
	);

	const header = (
		<div className="bg-blue-600 text-white p-4 flex items-center justify-between">
			<div className="flex items-center">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					className="h-6 w-6 mr-2"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
					/>
				</svg>
				<h2 className="text-xl font-bold">좌석 배치 확정</h2>
			</div>
			<button
				onClick={onClose}
				className="text-white hover:text-gray-200 focus:outline-none"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					className="h-6 w-6"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</button>
		</div>
	);

	const getObjectLabel = (object) => {
		if (!object) return "";
		const rawType = String(object.object_type || "");
		const base = OBJECT_LABELS[rawType] || rawType.toUpperCase();
		return object.object_id ? `${base}:${object.object_id}` : base;
	};

	const resolveLayoutColumns = (layoutConfig) => {
		if (Array.isArray(layoutConfig?.columns) && layoutConfig.columns.length > 0) {
			return layoutConfig.columns;
		}
		if (
			Array.isArray(layoutConfig?.seat_columns) &&
			layoutConfig.seat_columns.length > 0
		) {
			return layoutConfig.seat_columns;
		}
		return null;
	};

	const resolveNumbering = (layoutConfig, seatInfoByCode) => {
		const raw = String(layoutConfig?.numbering || "").toLowerCase();
		if (raw === "numeric" || raw === "alpha") {
			return raw;
		}
		const firstSeatCode = Object.keys(seatInfoByCode || {})[0];
		if (firstSeatCode && !isNaN(firstSeatCode)) {
			return "numeric";
		}
		return "alpha";
	};

	const resolveAisleAfter = (layoutConfig, columns) => {
		const raw =
			Number.isInteger(layoutConfig?.aisle_after) ?
				layoutConfig.aisle_after :
				Number.isInteger(layoutConfig?.aisleAfter) ?
					layoutConfig.aisleAfter :
					null;
		if (
			Number.isInteger(raw) &&
			raw > 0 &&
			Array.isArray(columns) &&
			raw < columns.length
		) {
			return raw;
		}
		return Math.floor((columns || []).length / 2);
	};

	const parseSeatCode = (seatCode, numbering, columns) => {
		if (!seatCode) return null;
		if (numbering === "numeric") {
			const seatNum = parseInt(seatCode, 10);
			if (Number.isNaN(seatNum)) return null;
			const cols =
				Array.isArray(columns) && columns.length > 0
					? columns
					: ["1", "2", "3", "4"];
			const colIndex = (seatNum - 1) % cols.length;
			return {
				row: Math.floor((seatNum - 1) / cols.length) + 1,
				col: String(cols[colIndex]),
			};
		}

		const match = String(seatCode).match(/^(\d+)([A-Za-z]+)$/);
		if (!match) return null;
		return {
			row: parseInt(match[1], 10),
			col: match[2],
		};
	};

	const resolveColumns = (layoutColumns, seatCols, numbering) => {
		if (Array.isArray(layoutColumns) && layoutColumns.length > 0) {
			return layoutColumns;
		}
		const derived = Array.from(seatCols || []);
		if (derived.length > 0) {
			if (numbering === "numeric") {
				derived.sort((a, b) => {
					const aNum = parseInt(a, 10);
					const bNum = parseInt(b, 10);
					if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
						return String(a).localeCompare(String(b));
					}
					return aNum - bNum;
				});
			} else {
				derived.sort((a, b) => String(a).localeCompare(String(b)));
			}
			return derived;
		}
		return numbering === "numeric"
			? ["1", "2", "3", "4"]
			: ["A", "B", "C", "D"];
	};

	const resolveRows = (layoutConfig, seatRows, fallbackRows) => {
		const start = parseInt(layoutConfig?.row_start, 10);
		const end = parseInt(layoutConfig?.row_end, 10);
		if (
			Number.isInteger(start) &&
			Number.isInteger(end) &&
			start <= end
		) {
			return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
		}

		const derived = Array.from(seatRows || [])
			.map((row) => parseInt(row, 10))
			.filter((row) => Number.isInteger(row))
			.sort((a, b) => a - b);
		if (derived.length > 0) {
			return derived;
		}

		if (Array.isArray(fallbackRows) && fallbackRows.length > 0) {
			return fallbackRows
				.map((row) => parseInt(row, 10))
				.filter((row) => Number.isInteger(row))
				.sort((a, b) => a - b);
		}

		return [];
	};

	function buildTableBody() {
		// 전체 열차에 대한 팀 카운트 계산
		const globalTeamCounts = {};
		// 각 사용자별 좌석 순번을 저장할 객체
		const userSeatNumbers = {};

		// 모든 호차에 대해 팀 카운트 초기화
		Object.entries(seatData).forEach(([carNo, carData]) => {
			Object.values(carData.data || {}).forEach((seatInfo) => {
				if (seatInfo.seat && seatInfo.seat !== "") {
					const userId = seatInfo.seat.split("@@")[0];
					if (!globalTeamCounts[userId]) {
						globalTeamCounts[userId] = seatInfo.total_cnt || 0;
						// 좌석 순번 초기화
						userSeatNumbers[userId] = [];
					}
					// 좌석 ID를 배열에 추가 (나중에 순서 결정에 사용)
					userSeatNumbers[userId].push(seatInfo.id);
				}
			});
		});

		const layoutCars = seatLayout?.cars || [];
		const layoutMap = new Map(
			layoutCars.map((car) => [String(car.car_number), car])
		);
		const layoutFallback = [...layoutCars];
		let fallbackIndex = 0;

		const carEntries = Object.entries(seatData).map(
			([carNo, carData]) => {
				let layoutCar = layoutMap.get(String(carNo));
				if (!layoutCar && layoutFallback[fallbackIndex]) {
					layoutCar = layoutFallback[fallbackIndex];
					fallbackIndex += 1;
				}
				return { carNo, carData, layoutCar };
			}
		);

		return (
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				modifiers={[restrictToWindowEdges]}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<div>
					{/* 호차는 세로로 배치 */}
					<div className="flex flex-col gap-4">
						{carEntries.map(({ carNo, carData, layoutCar }) => {
									// 각 호차별 팀 카운트는 전역 변수 사용
									const teamCounts = globalTeamCounts;
									const imageUrl = layoutCar?.image_url || null;
									const layoutConfig = layoutCar?.layout || {};
									const layoutColumns = resolveLayoutColumns(layoutConfig);
									const seatInfoByCode = carData.data || {};
									const numbering = resolveNumbering(layoutConfig, seatInfoByCode);

									const layoutObjects = Array.isArray(layoutCar?.objects)
										? layoutCar.objects
										: Array.isArray(layoutConfig.objects)
											? layoutConfig.objects
											: [];
									const seatObjects = layoutObjects.filter(
										(obj) => obj.object_type === "seat"
									);
									const nonSeatObjects = layoutObjects.filter(
										(obj) => obj.object_type !== "seat"
									);

									const seatCodeByRowCol = new Map();
									const seatRows = new Set();
									const seatCols = new Set();
									const seatDirectionByCode = new Map();
									const seatDirectionByRow = new Map();

									const registerSeat = (rowValue, colValue, seatCode, direction) => {
										const row = parseInt(rowValue, 10);
										const col =
											colValue === null || colValue === undefined
												? null
												: String(colValue);
										if (!Number.isInteger(row) || !col) {
											return;
										}
										const key = `${row}-${col}`;
										if (!seatCodeByRowCol.has(key)) {
											seatCodeByRowCol.set(key, seatCode);
										}
										if (seatCode && direction) {
											seatDirectionByCode.set(seatCode, direction);
										}
										if (direction && Number.isInteger(row) && !seatDirectionByRow.has(row)) {
											seatDirectionByRow.set(row, direction);
										}
										seatRows.add(row);
										seatCols.add(col);
									};

									const resolveWheelchairDirection = (obj) => {
										if (
											!obj ||
											obj.object_type !== "wheelchair_space" ||
											obj.direction
										) {
											return obj?.direction || null;
										}
										let row = parseInt(obj.row, 10);
										if (!Number.isInteger(row) && obj.position_hint) {
											const match = obj.position_hint.match(/rows?_([0-9]+)_([0-9]+)/i);
											if (match) {
												row = parseInt(match[1], 10);
											}
										}
										if (Number.isInteger(row)) {
											return seatDirectionByRow.get(row) || null;
										}
										return null;
									};

									if (seatObjects.length > 0) {
										seatObjects.forEach((obj) => {
											let rowValue = obj.row;
											let colValue = obj.col;
											let seatCode = obj.seat_code;

											if ((!rowValue || !colValue) && seatCode) {
												const parsed = parseSeatCode(
													seatCode,
													numbering,
													layoutColumns
												);
												if (parsed) {
													rowValue = rowValue || parsed.row;
													colValue = colValue || parsed.col;
												}
											}

											if (!seatCode && numbering === "alpha" && rowValue && colValue) {
												seatCode = `${rowValue}${colValue}`;
											}

											if (seatCode) {
												registerSeat(rowValue, colValue, seatCode, obj.direction);
											}
										});
									} else {
										Object.keys(seatInfoByCode).forEach((seatCode) => {
											const parsed = parseSeatCode(
												seatCode,
												numbering,
												layoutColumns
											);
											if (parsed) {
												registerSeat(parsed.row, parsed.col, seatCode, null);
											}
										});
									}

									const columns = resolveColumns(layoutColumns, seatCols, numbering);
									const aisleAfter = resolveAisleAfter(layoutConfig, columns);

									const objectByRowCol = new Map();
									const outsideObjects = [];
									const tableSlotsByAfterRow = new Map();
									const partitionByAfterRow = new Map();

									// 테이블용 between_rows 파싱 (left/right side 포함)
									const parseBetweenRowsHint = (hint) => {
										if (!hint) return null;
										const match = hint.match(
											/between_rows_(\d+)_(\d+)_(left|right)/i
										);
										if (!match) return null;
										return {
											afterRow: parseInt(match[1], 10),
											beforeRow: parseInt(match[2], 10),
											side: match[3].toLowerCase(),
										};
									};

									// 파티션용 between_rows 파싱 (side 없음)
									const parsePartitionHint = (hint) => {
										if (!hint) return null;
										const match = hint.match(
											/between_rows_(\d+)_(\d+)$/i
										);
										if (!match) return null;
										return {
											afterRow: parseInt(match[1], 10),
											beforeRow: parseInt(match[2], 10),
										};
									};

									nonSeatObjects.forEach((obj) => {
										const derivedDirection = resolveWheelchairDirection(obj);
										const normalizedObj =
											derivedDirection && !obj.direction
												? { ...obj, direction: derivedDirection }
												: obj;

										// 테이블 처리
										const betweenRows =
											normalizedObj.object_type === "table"
												? parseBetweenRowsHint(normalizedObj.position_hint)
												: null;
										if (betweenRows) {
											const slot =
												tableSlotsByAfterRow.get(betweenRows.afterRow) || {
													left: null,
													right: null,
												};
											slot[betweenRows.side] = normalizedObj;
											tableSlotsByAfterRow.set(betweenRows.afterRow, slot);
											return;
										}

										// 파티션 처리
										const partitionHint =
											normalizedObj.object_type === "partition"
												? parsePartitionHint(normalizedObj.position_hint)
												: null;
										if (partitionHint) {
											partitionByAfterRow.set(partitionHint.afterRow, normalizedObj);
											return;
										}

										const row = parseInt(normalizedObj.row, 10);
										const col =
											normalizedObj.col === null ||
											normalizedObj.col === undefined
												? null
												: String(normalizedObj.col);
										if (Number.isInteger(row) && col) {
											objectByRowCol.set(`${row}-${col}`, normalizedObj);
										} else if (normalizedObj.position_hint) {
											outsideObjects.push(normalizedObj);
										}
									});

									const rowNumbers = resolveRows(
										layoutConfig,
										seatRows,
										carData.rows
									);
									const orientation = String(
										layoutConfig.orientation || "horizontal"
									).toLowerCase();
									const isHorizontal = orientation === "horizontal";

									return (
										<div
											key={`car-${carNo}`}
											className="border p-4 rounded-lg w-full"
										>
											<h4 className="text-lg font-bold mb-4">
												{layoutCar?.label
													? `${carNo}호차 · ${layoutCar.label}`
													: `${carNo}호차`}
											</h4>
											{imageUrl && (
												<div className="mb-4 flex justify-center">
													<img
														src={imageUrl}
														alt={`${carNo}호차 배치도`}
														className="max-h-64 object-contain border rounded"
													/>
												</div>
											)}
											{outsideObjects.length > 0 && (
												<div className="mb-4 flex flex-wrap gap-2 text-xs text-gray-600">
													{outsideObjects.map((obj, idx) => (
														<span
															key={`${carNo}-outside-${idx}`}
															className="px-2 py-1 bg-gray-100 rounded"
															title={obj.notes || obj.position_hint || ""}
														>
															{getObjectLabel(obj)}
														</span>
													))}
												</div>
											)}

											{/* 좌석 배치 그리드 - 가운데 정렬 */}
											<div className="overflow-x-auto">
												<div className="min-w-max mx-auto">
													{isHorizontal ? (
														<SeatGridHorizontal
															rowNumbers={rowNumbers}
															columns={columns}
															aisleAfter={aisleAfter}
															seatCodeByRowCol={seatCodeByRowCol}
															seatDirectionByCode={seatDirectionByCode}
															objectByRowCol={objectByRowCol}
															seatInfoByCode={seatInfoByCode}
															teamCounts={teamCounts}
															userSeatNumbers={userSeatNumbers}
															tableSlotsByAfterRow={tableSlotsByAfterRow}
															partitionByAfterRow={partitionByAfterRow}
														/>
													) : (
														<div className="flex flex-col gap-4">
															{/* 열 레이블 */}
															{columns.length > 0 && (
																<div className="flex gap-2 items-center ml-8">
																	<div className="flex gap-2">
																		{columns
																			.slice(0, aisleAfter)
																			.map((col, idx) => (
																				<div
																					key={`${carNo}-col-left-${col}`}
																					className={`w-16 text-center font-bold ${
																						idx === 0 ? "ml-2" : ""
																					}`}
																				>
																					{col}
																				</div>
																			))}
																	</div>
																	<div className="w-8"></div>
																	<div className="flex gap-2">
																		{columns.slice(aisleAfter).map((col) => (
																			<div
																				key={`${carNo}-col-right-${col}`}
																				className="w-16 text-center font-bold"
																			>
																				{col}
																			</div>
																		))}
																	</div>
																</div>
															)}

															{/* 좌석 테이블 + 행 사이 테이블 바 */}
															{rowNumbers.map((rowNum, rowIdx) => (
																<React.Fragment key={`${carNo}-row-group-${rowNum}`}>
																	<SeatRow
																		key={`${carNo}-${rowNum}`}
																		row={rowNum}
																		columns={columns}
																		aisleAfter={aisleAfter}
																		seatCodeByRowCol={seatCodeByRowCol}
																		seatDirectionByCode={seatDirectionByCode}
																		objectByRowCol={objectByRowCol}
																		seatInfoByCode={seatInfoByCode}
																		teamCounts={teamCounts}
																		userSeatNumbers={userSeatNumbers}
																	/>
																	{/* 행 사이 테이블 바 */}
																	{tableSlotsByAfterRow.has(rowNum) && (
																		<InlineTableBar
																			tableSlot={tableSlotsByAfterRow.get(rowNum)}
																			columns={columns}
																			aisleAfter={aisleAfter}
																		/>
																	)}
																	{/* 행 사이 구분선/칸막이 */}
																	{partitionByAfterRow.has(rowNum) && (
																		<PartitionLine
																			partition={partitionByAfterRow.get(rowNum)}
																			columns={columns}
																			aisleAfter={aisleAfter}
																		/>
																	)}
																</React.Fragment>
															))}
														</div>
													)}
												</div>
											</div>
										</div>
									);
								})}
					</div>
				</div>
			</DndContext>
		);
	}

	/**
	 * 행 사이 구분선/칸막이 컴포넌트
	 */
	const PartitionLine = ({ partition, columns, aisleAfter }) => {
		if (!partition) return null;

		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);

		const leftColCount = aisleIndex;
		const rightColCount = safeColumns.length - aisleIndex;

		// 전체 너비 계산 (좌석 너비 64px + gap 8px + 통로 32px)
		const totalWidth = (leftColCount + rightColCount) * 64 + (safeColumns.length - 1) * 8 + 32;

		return (
			<div className="flex items-center my-2">
				{/* 행 번호 영역 (빈 공간) */}
				<div className="w-8"></div>

				{/* 구분선 영역 */}
				<div
					className="h-1.5 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 rounded-full shadow-sm relative"
					style={{ width: `${totalWidth}px` }}
					title={partition.notes || "구분선"}
				>
					{/* 중앙 장식 */}
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-gray-500 rounded-full border-2 border-white shadow"></div>
				</div>
			</div>
		);
	};

	/**
	 * 행 사이 테이블 바 컴포넌트 (마주보는 좌석 사이)
	 */
	const InlineTableBar = ({ tableSlot, columns, aisleAfter }) => {
		if (!tableSlot) return null;
		const { left, right } = tableSlot;

		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);

		const leftColCount = aisleIndex;
		const rightColCount = safeColumns.length - aisleIndex;

		// 테이블 바 너비 계산 (좌석 너비 64px + gap 8px)
		const leftBarWidth = leftColCount * 64 + (leftColCount - 1) * 8;
		const rightBarWidth = rightColCount * 64 + (rightColCount - 1) * 8;

		return (
			<div className="flex items-center my-1">
				{/* 행 번호 영역 (빈 공간) */}
				<div className="w-8"></div>

				{/* 테이블 바 영역 */}
				<div className="flex gap-2">
					{/* 왼쪽 테이블 바 */}
					<div className="flex gap-2">
						{left ? (
							<div
								className="h-3 bg-gradient-to-r from-amber-200 via-amber-300 to-amber-200 border border-amber-400 rounded-full shadow-sm flex items-center justify-center"
								style={{ width: `${leftBarWidth}px` }}
								title={left.notes || "테이블"}
							>
								<div className="w-1/2 h-1 bg-amber-400/50 rounded-full"></div>
							</div>
						) : (
							<div style={{ width: `${leftBarWidth}px` }} className="h-3"></div>
						)}
					</div>

					{/* 통로 */}
					<div className="w-8"></div>

					{/* 오른쪽 테이블 바 */}
					<div className="flex gap-2">
						{right ? (
							<div
								className="h-3 bg-gradient-to-r from-amber-200 via-amber-300 to-amber-200 border border-amber-400 rounded-full shadow-sm flex items-center justify-center"
								style={{ width: `${rightBarWidth}px` }}
								title={right.notes || "테이블"}
							>
								<div className="w-1/2 h-1 bg-amber-400/50 rounded-full"></div>
							</div>
						) : (
							<div style={{ width: `${rightBarWidth}px` }} className="h-3"></div>
						)}
					</div>
				</div>
			</div>
		);
	};

	/**
	 * 좌석 행 컴포넌트
	 */
	const SeatRow = ({
		row,
		columns,
		aisleAfter,
		seatCodeByRowCol,
		seatDirectionByCode,
		objectByRowCol,
		seatInfoByCode,
		teamCounts,
		userSeatNumbers,
	}) => {
		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);

		const leftColumns = safeColumns.slice(0, aisleIndex);
		const rightColumns = safeColumns.slice(aisleIndex);

		const renderCell = (col) => {
			const key = `${row}-${col}`;
			const seatCode = seatCodeByRowCol?.get
				? seatCodeByRowCol.get(key)
				: null;
			if (seatCode) {
				const seatInfo = seatInfoByCode
					? seatInfoByCode[seatCode]
					: null;
				const seatDirection = seatDirectionByCode?.get
					? seatDirectionByCode.get(seatCode)
					: null;
				return (
					<SeatCell
						key={`${key}-seat`}
						seatCode={seatCode}
						seatInfo={seatInfo}
						seatDirection={seatDirection}
						teamCounts={teamCounts}
						userSeatNumbers={userSeatNumbers}
					/>
				);
			}

			const object = objectByRowCol?.get ? objectByRowCol.get(key) : null;
			if (object) {
				if (object.object_type === "blocked") {
					return <BlankCell key={`${key}-blocked`} />;
				}
				return <ObjectCell key={`${key}-object`} object={object} />;
			}

			return <EmptyCell key={`${key}-empty`} />;
		};

		return (
			<div className="flex items-center mb-2">
				{/* 행 번호 표시 */}
				<div className="w-8 text-center font-bold">{row}</div>

				{/* 좌석 그룹 - 통로를 중심으로 2+2 배치 */}
				<div className="flex gap-2">
					{/* 왼쪽 좌석 (1, 2번 또는 A, B) */}
					<div className="flex gap-2">
						{leftColumns.map((col) => renderCell(col))}
					</div>

					{/* 통로 */}
					<div className="w-8"></div>

					{/* 오른쪽 좌석 (3, 4번 또는 C, D) */}
					<div className="flex gap-2">
						{rightColumns.map((col) => renderCell(col))}
					</div>
				</div>
			</div>
		);
	};

	const SeatGridHorizontal = ({
		rowNumbers,
		columns,
		aisleAfter,
		seatCodeByRowCol,
		seatDirectionByCode,
		objectByRowCol,
		seatInfoByCode,
		teamCounts,
		userSeatNumbers,
		tableSlotsByAfterRow,
		partitionByAfterRow,
	}) => {
		const safeRows = Array.isArray(rowNumbers) ? rowNumbers : [];
		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);
		const topColumns = safeColumns.slice(0, aisleIndex);
		const bottomColumns = safeColumns.slice(aisleIndex);
		const rowSlots = [];
		safeRows.forEach((rowNum) => {
			rowSlots.push({ type: "row", row: rowNum });
			if (tableSlotsByAfterRow?.has(rowNum)) {
				rowSlots.push({ type: "table", afterRow: rowNum });
			}
			if (partitionByAfterRow?.has(rowNum)) {
				rowSlots.push({ type: "partition", afterRow: rowNum });
			}
		});

		return (
			<div className="flex flex-col gap-2">
				{rowSlots.length > 0 && (
					<div className="flex items-center mb-2">
						<div className="w-8"></div>
						<div className="flex gap-2">
							{rowSlots.map((slot, idx) =>
								slot.type === "row" ? (
									<div
										key={`row-header-${slot.row}`}
										className="w-16 text-center font-bold"
									>
										{slot.row}
									</div>
								) : slot.type === "partition" ? (
									<div key={`row-header-partition-${idx}`} className="w-4 flex items-center justify-center">
										<div className="w-1 h-4 bg-gray-400 rounded-full"></div>
									</div>
								) : (
									<div key={`row-header-table-${idx}`} className="w-16"></div>
								)
							)}
						</div>
					</div>
				)}

				{topColumns.map((col, idx) => (
					<SeatRowHorizontal
						key={`seat-row-top-${col}`}
						col={col}
						rowSlots={rowSlots}
						seatCodeByRowCol={seatCodeByRowCol}
						seatDirectionByCode={seatDirectionByCode}
						objectByRowCol={objectByRowCol}
						seatInfoByCode={seatInfoByCode}
						teamCounts={teamCounts}
						userSeatNumbers={userSeatNumbers}
						tableSlotsByAfterRow={tableSlotsByAfterRow}
						partitionByAfterRow={partitionByAfterRow}
						side="left"
						isPrimaryRow={idx === 0}
					/>
				))}

				{bottomColumns.length > 0 && rowSlots.length > 0 && (
					<div className="flex items-center mb-2">
						<div className="w-8"></div>
						<div className="flex gap-2">
							{rowSlots.map((slot, idx) => (
								<div key={`aisle-${idx}-${slot.type}`} className={slot.type === "partition" ? "w-4 h-4" : "w-16 h-4"}></div>
							))}
						</div>
					</div>
				)}

				{bottomColumns.map((col, idx) => (
					<SeatRowHorizontal
						key={`seat-row-bottom-${col}`}
						col={col}
						rowSlots={rowSlots}
						seatCodeByRowCol={seatCodeByRowCol}
						seatDirectionByCode={seatDirectionByCode}
						objectByRowCol={objectByRowCol}
						seatInfoByCode={seatInfoByCode}
						teamCounts={teamCounts}
						userSeatNumbers={userSeatNumbers}
						tableSlotsByAfterRow={tableSlotsByAfterRow}
						partitionByAfterRow={partitionByAfterRow}
						side="right"
						isPrimaryRow={idx === 0}
					/>
				))}
			</div>
		);
	};

	const SeatRowHorizontal = ({
		col,
		rowSlots,
		seatCodeByRowCol,
		seatDirectionByCode,
		objectByRowCol,
		seatInfoByCode,
		teamCounts,
		userSeatNumbers,
		tableSlotsByAfterRow,
		partitionByAfterRow,
		side,
		isPrimaryRow,
	}) => {
		const safeSlots = Array.isArray(rowSlots) ? rowSlots : [];

		const renderSlot = (slot) => {
			if (slot.type === "table") {
				const tableSlot = tableSlotsByAfterRow?.get
					? tableSlotsByAfterRow.get(slot.afterRow)
					: null;
				const tableObject = tableSlot ? tableSlot[side] : null;
				if (tableObject && isPrimaryRow) {
					return (
						<ObjectCell
							key={`table-${side}-${slot.afterRow}`}
							object={tableObject}
						/>
					);
				}
				return <TableSpacer key={`table-spacer-${side}-${slot.afterRow}`} />;
			}

			if (slot.type === "partition") {
				return (
					<div key={`partition-${slot.afterRow}`} className="w-4 h-12 flex items-center justify-center">
						<div className="w-1 h-full bg-gray-400 rounded-full"></div>
					</div>
				);
			}

			const key = `${slot.row}-${col}`;
			const seatCode = seatCodeByRowCol?.get
				? seatCodeByRowCol.get(key)
				: null;
			if (seatCode) {
				const seatInfo = seatInfoByCode
					? seatInfoByCode[seatCode]
					: null;
				const seatDirection = seatDirectionByCode?.get
					? seatDirectionByCode.get(seatCode)
					: null;
				return (
					<SeatCell
						key={`${key}-seat`}
						seatCode={seatCode}
						seatInfo={seatInfo}
						seatDirection={seatDirection}
						teamCounts={teamCounts}
						userSeatNumbers={userSeatNumbers}
					/>
				);
			}

			const object = objectByRowCol?.get ? objectByRowCol.get(key) : null;
			if (object) {
				if (object.object_type === "blocked") {
					return <BlankCell key={`${key}-blocked`} />;
				}
				return <ObjectCell key={`${key}-object`} object={object} />;
			}

			return <EmptyCell key={`${key}-empty`} />;
		};

		return (
			<div className="flex items-center mb-2">
				<div className="w-8 text-center font-bold">{col}</div>
				<div className="flex gap-2">
					{safeSlots.map((slot) => renderSlot(slot))}
				</div>
			</div>
		);
	};

	/**
	 * 드래그 가능한 좌석 컴포넌트
	 */
	const DraggableSeat = ({
		id,
		seat,
		name,
		is_previous_seat,
		total_cnt,
		children,
	}) => {
		// 이전 좌석은 드래그 불가
		if (is_previous_seat) {
			return <>{children}</>;
		}

		// useDraggable 후크 사용
		const { attributes, listeners, setNodeRef, transform, isDragging } =
			useDraggable({
				id: id,
				data: {
					id,
					seat,
					name,
					is_previous_seat,
					total_cnt,
				},
			});

		// 드래그 시 스타일 적용
		const style = transform
			? {
					transform: CSS.Transform.toString(transform),
					opacity: isDragging ? 0.5 : 1,
					zIndex: isDragging ? 1000 : 1,
					cursor: "grab",
			  }
			: { cursor: "grab" };

		return (
			<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
				{children}
			</div>
		);
	};

	/**
	 * 드롭 가능한 좌석 컴포넌트
	 */
	const DroppableSeat = ({ id, children }) => {
		// useDroppable 후크 사용
		const { isOver, setNodeRef } = useDroppable({
			id: id,
		});

		// 드롭 가능 영역 위에 드래그 중일 때 스타일 적용
		const style = {
			backgroundColor: isOver ? "rgba(59, 130, 246, 0.1)" : undefined,
			borderColor: isOver ? "rgb(59, 130, 246)" : undefined,
			borderWidth: isOver ? "2px" : undefined,
		};

		return (
			<div ref={setNodeRef} style={style}>
				{children}
			</div>
		);
	};

	const EmptyCell = ({ label, direction }) => (
		<div className="w-16 h-12 bg-gray-100 border border-gray-300 rounded-md shadow-sm flex items-center justify-center text-gray-400 redips-mark relative">
			{label ? (
				<span className="text-[11px] text-gray-400">{label}</span>
			) : (
				<span className="text-gray-300"></span>
			)}
			<DirectionBadge direction={direction} />
		</div>
	);

	const BlankCell = () => <div className="w-16 h-12"></div>;

	const ObjectCell = ({ object }) => {
		if (!object) return null;
		if (object.object_type === "table") {
			return <TableCell object={object} />;
		}
		if (object.object_type === "wheelchair_space") {
			return <WheelchairCell object={object} />;
		}
		if (object.object_type === "blocked") {
			return <BlockedCell object={object} />;
		}
		// 특수 시설 (무대, 와인바, 방송실)
		if (["stage", "wine_bar", "broadcast_room"].includes(object.object_type)) {
			const styleClass = OBJECT_STYLES[object.object_type] || OBJECT_STYLES.container;
			return (
				<div
					className={styleClass}
					title={object?.notes || object?.position_hint || ""}
				>
					<span>{getObjectLabel(object)}</span>
				</div>
			);
		}
		return (
			<div
				className={OBJECT_STYLES.container}
				title={object?.notes || object?.position_hint || ""}
			>
				<span>{getObjectLabel(object)}</span>
			</div>
		);
	};

	const TableSpacer = () => <div className="w-16 h-12"></div>;

	const TableCell = ({ object }) => (
		<div
			className="w-16 h-12 flex items-center justify-center"
			title={object?.notes || object?.position_hint || ""}
		>
			<div className="flex flex-col items-center gap-0.5">
				<div className="w-8 h-2 bg-amber-200 border border-amber-400 rounded-full"></div>
				<div className="w-6 h-1 bg-amber-300 rounded-full"></div>
			</div>
		</div>
	);

	const WheelchairCell = ({ object }) => {
		const dir = String(object?.direction || "").toLowerCase();
		const styleClass =
			dir === "backward"
				? OBJECT_STYLES.wheelchairBackward
				: OBJECT_STYLES.wheelchairForward;
		return (
			<div
				className={`${styleClass} relative`}
				title={object?.notes || object?.position_hint || ""}
			>
				<span className="text-base" role="img" aria-label="wheelchair">
					♿
				</span>
				<DirectionBadge direction={object?.direction} />
			</div>
		);
	};

	const BlockedCell = ({ object }) => (
		<div
			className="w-16 h-12 bg-gray-200 border border-gray-300 rounded-md shadow-sm relative overflow-hidden"
			title={object?.notes || object?.position_hint || object?.object_id || ""}
		>
			<div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_45%,rgba(107,114,128,0.5)_45%,rgba(107,114,128,0.5)_55%,transparent_55%)]"></div>
		</div>
	);

	const getDirectionKey = (direction) => {
		const normalized = String(direction || "").toLowerCase();
		if (normalized === "backward") return "backward";
		if (normalized === "forward") return "forward";
		if (normalized === "left") return "left";
		if (normalized === "right") return "right";
		return "unknown";
	};

	const directionPalette = {
		forward: {
			assigned: "bg-emerald-500 border-emerald-600 text-white",
			available: "border-emerald-500 text-emerald-600",
		},
		backward: {
			assigned: "bg-rose-400 border-rose-500 text-white",
			available: "border-rose-400 text-rose-500",
		},
		left: {
			assigned: "bg-amber-500 border-amber-600 text-white",
			available: "border-amber-500 text-amber-600",
		},
		right: {
			assigned: "bg-sky-500 border-sky-600 text-white",
			available: "border-sky-500 text-sky-600",
		},
		unknown: {
			assigned: "bg-slate-400 border-slate-500 text-white",
			available: "border-slate-400 text-slate-500",
		},
	};

	// 방향 화살표 배지 (↑↓←→)
	const DirectionBadge = ({ direction }) => {
		const normalized = String(direction || "").toLowerCase();
		const arrows = {
			forward: "↑",
			backward: "↓",
			left: "←",
			right: "→",
		};
		const arrow = arrows[normalized];
		if (!arrow) return null;
		return (
			<span className="absolute -top-1 -right-1 text-[10px] font-bold opacity-60">
				{arrow}
			</span>
		);
	};

	// 좌석 등받이 표시 (방향에 따라 위치 변경)
	const SeatBackrest = ({ direction }) => {
		const normalized = String(direction || "").toLowerCase();
		const config = {
			forward: { position: "-left-2 top-2", size: "w-2 h-7", color: "bg-emerald-600/40" },
			backward: { position: "-right-2 top-2", size: "w-2 h-7", color: "bg-rose-500/40" },
			left: { position: "left-2 -top-2", size: "h-2 w-7", color: "bg-amber-500/40" },
			right: { position: "left-2 -bottom-2", size: "h-2 w-7", color: "bg-sky-500/40" },
		};
		const c = config[normalized] || { position: "-left-2 top-2", size: "w-2 h-7", color: "bg-slate-400/30" };
		return (
			<div className={`absolute ${c.position} ${c.size} rounded-sm ${c.color}`} />
		);
	};

	/**
	 * 개별 좌석 셀 컴포넌트
	 */
	const SeatCell = ({
		seatCode,
		seatInfo,
		seatDirection,
		teamCounts,
		userSeatNumbers,
	}) => {
		// 좌석 정보가 없는 경우 (사용 불가 좌석)
		if (!seatInfo || seatInfo.is_disabled) {
			return <EmptyCell label={seatCode} direction={seatDirection} />;
		}

		const {
			id,
			seat,
			name,
			last_phone,
			is_previous_seat,
			total_cnt,
			is_disabled,
		} = seatInfo;

		// 빈 좌석인 경우 (사용 불가 좌석)
		if (seat === null) {
			return <EmptyCell label={seatCode} direction={seatDirection} />;
		}

		// 드래그 이벤트 핸들러
		const handleDragStart = (e) => {
			// 이전 좌석(is_previous_seat=true)은 드래그 불가
			if (is_previous_seat) {
				e.preventDefault();
				return;
			}

			// 드래그 데이터 설정
			e.dataTransfer.setData(
				"text/plain",
				JSON.stringify({
					id,
					seat,
					name,
					is_previous_seat,
					total_cnt,
				})
			);

			// 드래그 중인 좌석 상태 업데이트
			setDraggedSeat(id);

			// 드래그 이미지 설정 (시각적 피드백)
			const dragIcon = e.currentTarget.cloneNode(true);
			dragIcon.style.opacity = "0.7";
			dragIcon.style.position = "absolute";
			dragIcon.style.top = "-1000px";
			dragIcon.style.width = "60px";
			dragIcon.style.height = "60px";
			dragIcon.style.zIndex = "9999";
			dragIcon.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)";
			document.body.appendChild(dragIcon);
			e.dataTransfer.setDragImage(dragIcon, 30, 30);

			// 드래그 효과 설정
			e.dataTransfer.effectAllowed = "move";

			// 타임아웃으로 드래그 이미지용 요소 제거
			setTimeout(() => {
				if (document.body.contains(dragIcon)) {
					document.body.removeChild(dragIcon);
				}
			}, 10);

			// 원본 요소에 드래그 중 스타일 적용
			e.currentTarget.classList.add("opacity-50", "scale-95");
		};

		// 드래그 오버 이벤트 핸들러 (빈 좌석 또는 배정된 좌석 모두 드롭 가능 - swap 지원)
		const handleDragOver = (e) => {
			// is_previous_seat는 드롭 불가 (확정된 이전 배정)
			if (is_previous_seat) return;
			e.preventDefault();
			e.stopPropagation();

			// 드롭 가능 시각적 피드백
			e.currentTarget.classList.add(
				"bg-blue-100",
				"border-blue-500",
				"border-2"
			);
			e.dataTransfer.dropEffect = "move";

			setDragOverSeat(id);
		};

		// 드래그 엔터 이벤트 핸들러
		const handleDragEnter = (e) => {
			if (is_previous_seat) return;
			e.preventDefault();
			e.stopPropagation();

			// 드롭 가능 시각적 피드백
			e.currentTarget.classList.add(
				"bg-blue-100",
				"border-blue-500",
				"border-2"
			);

			setDragOverSeat(id);
		};

		// 드래그 리브 이벤트 핸들러
		const handleDragLeave = (e) => {
			if (!is_previous_seat && !e.currentTarget.contains(e.relatedTarget)) {
				// 드롭 가능 시각적 피드백 제거
				e.currentTarget.classList.remove(
					"bg-blue-100",
					"border-blue-500",
					"border-2"
				);

				if (dragOverSeat === id) {
					setDragOverSeat(null);
				}
			}
		};

		// 드롭 이벤트 핸들러 (빈 좌석 이동 + 배정된 좌석 swap 모두 지원)
		const handleDrop = (e) => {
			e.preventDefault();
			e.stopPropagation();

			// is_previous_seat는 드롭 불가
			if (is_previous_seat) {
				return;
			}

			// 드롭 가능 시각적 피드백 제거
			e.currentTarget.classList.remove(
				"bg-blue-100",
				"border-blue-500",
				"border-2"
			);

			try {
				// 드래그된 좌석 데이터 가져오기
				const draggedSeatData = JSON.parse(
					e.dataTransfer.getData("text/plain")
				);

				// 좌석 데이터 업데이트
				updateSeatData(draggedSeatData.id, id);

				// 드래그 상태 초기화
				setDraggedSeat(null);
				setDragOverSeat(null);
			} catch (error) {
				console.error("좌석 이동 중 오류 발생:", error);
			}
		};

		// 드래그 종료 이벤트 핸들러
		const handleDragEnd = (e) => {
			// 드래그 중 스타일 제거
			e.currentTarget.classList.remove("opacity-50", "scale-95");

			// 드래그 상태 초기화
			setDraggedSeat(null);
			setDragOverSeat(null);

			// 모든 드롭 영역의 하이라이트 제거
			document
				.querySelectorAll(`.${SEAT_STYLES.available.container}`)
				.forEach((el) => {
					el.classList.remove(
						"bg-blue-100",
						"border-blue-500",
						"border-2"
					);
				});
		};

		// 배정 가능한 좌석
		if (seat === "") {
			const palette = directionPalette[getDirectionKey(seatDirection)];
			const availableClass = `w-16 h-12 bg-white border-2 rounded-md shadow-sm flex flex-col items-center justify-center transition-all duration-200 ${palette.available}`;
			// 비어있는 좌석은 드롭 가능한 영역으로 처리
			const emptySeatContent = (
				<div
					className={`${availableClass} relative overflow-visible`}
					data-droppable="true"
					onDragOver={handleDragOver}
					onDragEnter={handleDragEnter}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					<SeatBackrest direction={seatDirection} />
					<DirectionBadge direction={seatDirection} />
					<input type="hidden" name="datavalue" value={seat} />
					<div
						id={`${id}`}
						className="w-full h-full flex items-center justify-center relative z-10"
					>
						<div className="C text-center">
							<span className="text-[10px] font-semibold">
								{seatCode}
							</span>
						</div>
					</div>
				</div>
			);

			// dnd-kit의 DroppableSeat으로 감싸기
			return <DroppableSeat id={id}>{emptySeatContent}</DroppableSeat>;
		}

		// 배정된 좌석
		if (seat && seat !== "") {
			const palette = directionPalette[getDirectionKey(seatDirection)];
			const assignedClass = `w-16 h-12 rounded-md shadow-sm flex flex-col items-center justify-center transition-all border ${palette.assigned}`;
			const userId = seat.split("@@")[0];
			const userName = name || seat.split("@@")[1] || "";

			// 이전 좌석인지 여부에 따라 스타일 조정
			const containerClass = is_previous_seat
				? `${assignedClass} cursor-default opacity-50`
				: `${assignedClass} cursor-move hover:shadow-md`;

			// 사용자별 좌석 순번을 표시 (안전하게 처리)
			let userSeatNumber = 0;
			let totalSeats = 0;

			if (userSeatNumbers && userSeatNumbers[userId]) {
				userSeatNumber = userSeatNumbers[userId].indexOf(id) + 1;
			}

			if (teamCounts && teamCounts[userId]) {
				totalSeats = teamCounts[userId];
			} else if (total_cnt) {
				totalSeats = total_cnt;
			}

			// 배정된 좌석 콘텐츠 (드래그 + 드롭 타겟으로 swap 지원)
			const assignedSeatContent = (
				<div
					className={`${containerClass} relative overflow-visible`}
					draggable={!is_previous_seat}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
					onDragOver={handleDragOver}
					onDragEnter={handleDragEnter}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					<SeatBackrest direction={seatDirection} />
					<DirectionBadge direction={seatDirection} />
					<div
						id={`${id}`}
						className={`C${userId} w-full h-full flex flex-col items-center justify-center rounded-[6px] overflow-hidden relative z-10`}
					>
						<span className="text-[10px] font-medium truncate w-full text-center text-white leading-tight">
							{userName}
						</span>
						{last_phone && (
							<span className="text-[9px] text-white/80 leading-tight">
								({last_phone})
							</span>
						)}
						<span className="text-[9px] text-white/80 leading-tight">
							{userSeatNumber}/{totalSeats}
						</span>
					</div>
				</div>
			);

			// 이전 좌석은 그대로 리턴, 그 외에는 DraggableSeat + DroppableSeat으로 감싸기 (swap 지원)
			if (is_previous_seat) {
				return assignedSeatContent;
			} else {
				return (
					<DroppableSeat id={id}>
						<DraggableSeat
							id={id}
							seat={seat}
							name={name}
							is_previous_seat={is_previous_seat}
							total_cnt={total_cnt}
						>
							{assignedSeatContent}
						</DraggableSeat>
					</DroppableSeat>
				);
			}
		}
	};

	const IndexBox = (
		<div className="bg-gray-100 p-4 rounded-lg mb-4">
			<div className="flex items-center space-x-6">
				<div className="flex items-center">
					<div className={SEAT_STYLES.legend.empty}></div>
					<span className="text-sm">빈 좌석</span>
				</div>
				<div className="flex items-center">
					<div className={SEAT_STYLES.legend.previous}></div>
					<span className="text-sm">배정 완료</span>
				</div>
				<div className="flex items-center">
					<div className={SEAT_STYLES.legend.available}></div>
					<span className="text-sm">배정 가능</span>
				</div>
				<div className="flex items-center">
					<div className={SEAT_STYLES.legend.assigned}></div>
					<span className="text-sm">배정 중</span>
				</div>
			</div>
		</div>
	);

	return (
		<div
			className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
			onClick={handleOverlayClick}
		>
			<div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-6xl max-h-[94vh] overflow-hidden flex flex-col">
				{/* 모달 헤더 */}
				{header}

				{/* 모달 본문 - 스크롤 가능한 영역 */}
				<div className="flex-1 overflow-y-auto p-4">
					{/* 범례 */}
					{IndexBox}

					{/* 좌석 테이블 */}
					<div className="mb-6">
						{/* 열차 정보가 없으면 안내 메시지 표시 */}
						{Object.keys(seatData).length === 0 ? (
							<div className="text-center p-4">
								열차 정보가 없습니다.
							</div>
						) : (
							buildTableBody()
						)}
					</div>
				</div>

				{/* 모달 푸터 - 항상 하단에 고정 */}
				{footer}

				{/* 로딩 모달 */}
				{showModal && (
					<>
						<div
							style={{
								position: "fixed",
								top: 0,
								left: 0,
								width: "100vw",
								height: "100vh",
								backgroundColor: "rgba(0, 0, 0, 0.8)",
								zIndex: 9000,
							}}
						/>
						<div className="fixed inset-0 flex items-center justify-center z-[9001]">
							<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full p-6">
								{/* 모달 내용 */}
								<div className="p-6 text-center">
									{modalType === "exec" && (
										<div className="flex flex-col items-center">
											<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
											<div className="text-lg font-medium">
												좌석배치 실행 중...
											</div>
											<div className="mt-2 text-gray-600">
												실행 시간: {timer.minutes}분{" "}
												{timer.seconds}초
											</div>
										</div>
									)}
									{modalType === "cancel" && (
										<div className="text-lg font-medium">
											실행이 완료되었습니다.
										</div>
									)}
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
